import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, Loader2, ClipboardList } from 'lucide-react';
import axios from 'axios';
import { isQuestionVisible, computeCompletion } from '../lib/questionnaireLogic';
import QuestionField from '../components/QuestionField';

// Public client without credentials/auth — required because the server uses
// `Access-Control-Allow-Origin: *` which is incompatible with `withCredentials`.
const publicApi = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: false,
  headers: { 'Content-Type': 'application/json' },
});

export default function QuestionnairePublic() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await publicApi.get(`/public/questionnaires/${token}`);
        setInfo(data);
        if (data.submitted_at) setSubmitted(true);
      } catch (err) {
        setError(err?.response?.data?.detail || 'Lien invalide ou expiré.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const setAnswer = (qid, value) => setAnswers((prev) => ({ ...prev, [qid]: value }));

  // Wave 1u9k — shared visibility & completion engine
  const isVisible = (q) => isQuestionVisible(q, info?.questions || [], answers);
  const completion = useMemo(() => computeCompletion(info?.questions || [], answers), [info, answers]);

  const allRequiredAnswered = useMemo(() => {
    if (!info?.questions) return true;
    return info.questions.every((q) => {
      if (!q.required) return true;
      if (!isVisible(q)) return true; // hidden conditional questions don't block submit
      const v = answers[q.id];
      if (q.type === 'multi') return Array.isArray(v) && v.length > 0;
      return v !== undefined && v !== null && String(v).trim() !== '';
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, info]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || submitted || !allRequiredAnswered) return;
    setSubmitting(true);
    try {
      await publicApi.post(`/public/questionnaires/${token}/submit`, { answers });
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Envoi impossible. Réessayez.');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFE]">
        <Loader2 className="w-8 h-8 animate-spin text-[#4F46E5]" />
      </div>
    );
  }

  if (error && !info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFE] p-6">
        <div className="max-w-md w-full bg-white rounded-2xl border border-[#FECACA] p-8 text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-[#FEE2E2] text-[#B91C1C] flex items-center justify-center mb-4">!</div>
          <h1 className="text-[20px] font-bold text-[#1A1B2E] mb-2">Lien invalide</h1>
          <p className="text-[13.5px] text-[#6B6C85]">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFE] p-6">
        <div className="max-w-md w-full bg-white rounded-2xl border border-[#A7F3D0] p-8 text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-[#E6FBF7] text-[#0F766E] flex items-center justify-center mb-4">
            <CheckCircle2 className="w-7 h-7" />
          </div>
          <h1 className="text-[20px] font-bold text-[#1A1B2E] mb-2">Réponses enregistrées</h1>
          <p className="text-[13.5px] text-[#6B6C85]">Merci pour votre temps. Vos réponses ont bien été transmises à l'organisme de formation.</p>
        </div>
      </div>
    );
  }

  const session = info.session || {};
  const branding = info.branding || {};
  const primary = branding.primary || '#4F46E5';
  const secondary = branding.secondary || '#14B8A6';
  const headerStyle = { background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)` };
  const submitStyle = { background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)` };
  const visibleQuestions = (info.questions || []).filter(isVisible);
  return (
    <div className="min-h-screen bg-[#FAFAFE] py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-[#EFF0F6] overflow-hidden">
          <div className="px-6 py-5 text-white" style={headerStyle}>
            {branding.logo_b64 && (
              <div className="flex items-center gap-3 mb-3">
                <img src={branding.logo_b64.startsWith('data:') ? branding.logo_b64 : `data:image/png;base64,${branding.logo_b64}`} alt={branding.org_name} className="h-9 w-auto rounded-lg bg-white/95 p-1.5 shadow-sm" />
                <div className="text-[13px] font-semibold text-white/95">{branding.org_name}</div>
              </div>
            )}
            <div className="flex items-center gap-2.5 text-white/90 text-[12px] font-semibold uppercase tracking-wider mb-1">
              <ClipboardList className="w-3.5 h-3.5" />
              {info.kind === 'positioning' ? 'Questionnaire de positionnement' : info.kind === 'satisfaction' ? 'Questionnaire de satisfaction' : 'Questionnaire'}
            </div>
            <h1 className="text-[22px] font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{info.title}</h1>
            {(session.title || session.startDate) && (
              <p className="text-[13px] text-white/85 mt-1.5">
                {session.title || session.program} {session.startDate ? `· démarre le ${new Date(session.startDate).toLocaleDateString('fr-FR')}` : ''}
              </p>
            )}
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Wave 1u9k — sticky progress bar (mirrors company-side public page) */}
            {visibleQuestions.length > 0 && (
              <div className="sticky top-0 -mx-6 px-6 py-2.5 bg-white/95 backdrop-blur border-b border-[#EFF0F6] z-10">
                <div className="flex items-center justify-between gap-3 mb-1">
                  <div className="text-[12px] text-[#4A4B63]">
                    <span className="font-bold tabular-nums text-[#1A1B2E]">{completion.reqDone}</span>
                    <span className="text-[#6B6C85]"> / {completion.totalRequired || '0'} obligatoires</span>
                    {completion.optDone > 0 && (
                      <span className="text-[#6B6C85]"> · {completion.optDone} facultatives</span>
                    )}
                  </div>
                  <span className="text-[12px] font-bold tabular-nums" style={{ color: completion.allRequiredOk ? '#0F766E' : primary }}>
                    {completion.pct}%
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-[#F6F7FB] overflow-hidden">
                  <div
                    data-testid="questionnaire-progress-bar"
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${completion.pct}%`,
                      background: completion.allRequiredOk
                        ? 'linear-gradient(90deg, #14B8A6, #0F766E)'
                        : `linear-gradient(90deg, ${primary}80, ${primary})`,
                    }}
                  />
                </div>
              </div>
            )}

            {info.learner_name && (
              <div className="text-[12.5px] text-[#6B6C85]">Bonjour <span className="font-semibold text-[#1A1B2E]">{info.learner_name}</span>, merci de répondre aux questions ci-dessous.</div>
            )}
            {info.description && <p className="text-[13px] text-[#4A4B63] bg-[#FAFAFE] rounded-xl p-3 border border-[#EFF0F6]">{info.description}</p>}
            {visibleQuestions.map((q, idx) => (
              <QuestionField
                key={q.id}
                idx={idx + 1}
                question={q}
                value={answers[q.id]}
                onChange={(v) => setAnswer(q.id, v)}
                primary={primary}
              />
            ))}
            {error && <div className="text-[12.5px] text-[#B91C1C]">{error}</div>}
            <button
              type="submit"
              disabled={submitting || !allRequiredAnswered}
              data-testid="questionnaire-submit"
              style={submitStyle}
              className="w-full h-12 rounded-xl text-white text-[14px] font-semibold disabled:opacity-50 hover:opacity-95 transition-opacity flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {submitting ? 'Envoi…' : 'Envoyer mes réponses'}
            </button>
            <p className="text-[11px] text-[#8E8FA5] text-center">{branding.org_name ? `Vos réponses sont confidentielles et utilisées uniquement par ${branding.org_name}.` : 'Vos réponses sont confidentielles et utilisées uniquement par l\'organisme de formation.'}</p>
          </form>
        </div>
      </div>
    </div>
  );
}
